import { type PricedProduct } from '@agos/customer-core'
import { T, formatCents, tt } from '@agos/shared'
import { cn } from '@agos/ui'
import { Boxes, ChevronDown } from 'lucide-react'
import { useState } from 'react'

import {
  EMPTY_BOX,
  boxLines,
  boxProblem,
  fixedItemsFor,
  optionsFor,
  type BoxConfig,
  type BoxSelection,
} from './burger-box'

/**
 * The burger box builder — a slot per course, each filled from its own category.
 *
 * Priced as the **sum** of what is in it, which is not the kombo's rule and the difference is real: a
 * kombo is three pizzas instead of one, a box is four items together. The line the basket gets is one
 * per item under a shared group id, because the kitchen makes a burger *and* a portion of fries and the
 * order has to name both — `removeBox` is what takes them out as one thing again.
 *
 * The slot rules are in `burger-box.ts` and tested there, including the one that matters on this data:
 * every slot is `required: false`, so the check that the box has *anything* in it comes before the
 * check that each required slot is filled. FB-190 is the manager's version of getting that order wrong.
 */
export function BoxBuilder({
  config,
  products,
  onAdd,
  onAdded,
}: {
  config: BoxConfig
  products: readonly PricedProduct[]
  onAdd: (selection: BoxSelection) => void
  /** §9.87 — the box reached the cart. A panel has nothing to close. */
  onAdded: () => void
}) {
  const [selection, setSelection] = useState<BoxSelection>(EMPTY_BOX)
  /* §9.646 - the config decides how it **opens**; once expanded it stays so while the customer is using it. A
     setting that re-collapsed on every render would be a control fighting the person using it, and the owner's
     words are *"by default"*. */
  const [open, setOpen] = useState(!config.collapsedByDefault)
  const problem = boxProblem(config, selection)

  const priceOf = (productId: number): number => {
    const product = products.find((candidate) => candidate.id === productId)
    if (product === undefined) return 0
    return typeof product.price === 'number'
      ? Math.round(product.price * 100)
      : Math.round(Number(product.price) * 100) || 0
  }
  /**
   * What the box costs - §10A.107, the owner's item E.
   *
   * The sum of what is in it, or the fixed price the owner set. **This panel is where a customer
   * decides**, so showing the sum while `ServerPricing` bills the fixed price would quote them one
   * price and charge another - which is the whole reason the fixed price is applied on the server and
   * read here rather than the other way round.
   *
   * An empty box still shows nothing rather than the fixed price: a box with no content cannot be
   * added (`boxProblem`), and pricing an empty panel would read as a charge for nothing.
   */
  const parts = boxLines(config, selection)
  const summed = parts.reduce((sum, line) => sum + priceOf(line.productId), 0)
  const total =
    config.fixedPriceCents !== null && config.fixedPriceCents > 0 && parts.length > 0
      ? config.fixedPriceCents
      : summed

  // §9.87 — a panel in the page, not a dialog over it. Legacy's `bb-panel` is simply there when the
  // customer is in the box's category: no overlay, nothing to open, nothing to close.
  return (
    <div className="max-w-5xl mx-auto px-4 mt-6" data-box-modal={config.id}>
      <div
        aria-label={config.name}
        className="bg-card border border-border rounded-3xl shadow-sm w-full flex flex-col overflow-hidden"
      >
        {/* §9.646, owner O37 - the header is the control. A four-slot builder sitting open in the middle of the
            menu crowds everything else out, and the owner asked for it closed until somebody wants it:
            *"that combo box should be collapsable, and collapsed by default"*. */}
        <button
          type="button"
          data-box-toggle={config.id}
          aria-expanded={open}
          onClick={() => setOpen(!open)}
          className="w-full flex items-center justify-between px-6 py-5 border-b border-border shrink-0 text-left"
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-2xl bg-green-100 flex items-center justify-center shrink-0">
              <Boxes className="w-5 h-5 text-green-600" />
            </div>
            <div className="text-left min-w-0">
              <h2 className="font-bold text-lg leading-tight truncate">{config.name}</h2>
              {config.description !== '' && (
                <p className="text-xs text-muted-foreground line-clamp-2">{config.description}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {/* The box's own price stays on the header, because a collapsed section nobody can identify is
                worse than a long one. Only when the box has a fixed price: a sum of nothing chosen is 0,00. */}
            {config.fixedPriceCents !== null && config.fixedPriceCents > 0 && (
              <span className="text-sm font-bold">{formatCents(config.fixedPriceCents)}</span>
            )}
            <ChevronDown
              className={cn('w-5 h-5 text-muted-foreground transition-transform', open && 'rotate-180')}
            />
          </div>
        </button>

        {/* §9.646 - not rendered rather than hidden with a class. A collapsed box keeps its slots' `<select>`
            elements out of the document entirely, which is also what makes *collapsed* assertable: a hidden
            panel and an absent one read the same to a customer and differently to a test. The selection lives in
            this component, so it survives a collapse. */}
        {open && (
        <>
        <div className="px-6 py-5 space-y-3 overflow-y-auto flex-1">
          {config.slots.map((slot) => {
            const options = optionsFor(slot, products as unknown as Array<PricedProduct & { categoryId?: number }>)
            /* FB-850. What is in this slot whatever the customer does. `boxProblem` counts such a slot as
               satisfied, so the asterisk defers to the same rule rather than telling somebody to choose from
               a slot that is already full. */
            const included = fixedItemsFor(slot, products)
            return (
              <label key={slot.id} className="block space-y-1">
                <span className="text-xs font-semibold text-muted-foreground">
                  {slot.label}
                  {slot.required && included.length === 0 && (
                    <span className="text-destructive"> *</span>
                  )}
                </span>
                {included.length > 0 && (
                  <span className="block text-[11px] text-emerald-700" data-box-slot-fixed={slot.id}>
                    <T>Vždy zahrnuté</T>: {included.map((product) => product.name).join(', ')}
                  </span>
                )}
                <div className="relative">
                  <select
                    data-box-slot={slot.id}
                    value={selection[slot.id] === undefined || selection[slot.id] === null ? '' : String(selection[slot.id])}
                    onChange={(event) => {
                      const value = event.target.value
                      setSelection({ ...selection, [slot.id]: value === '' ? null : Number(value) })
                    }}
                    className="w-full appearance-none rounded-2xl border border-border bg-background px-4 py-3 pr-10 text-sm font-medium"
                  >
                    <option value="">{tt('Nevyberať')}</option>
                    {options.map((product) => (
                      <option key={product.id} value={product.id}>
                        {product.name} · {formatCents(priceOf(product.id))}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                </div>
                {options.length === 0 && (
                  <span className="text-[11px] text-amber-600" data-box-slot-empty={slot.id}>
                    <T>V tejto kategórii momentálne nič nie je.</T>
                  </span>
                )}
              </label>
            )
          })}
        </div>

        <div className="px-6 pb-6 pt-3 space-y-3 border-t border-border shrink-0">
          {problem !== '' && (
            <p className="text-xs text-amber-600" data-box-problem>
              {problem}
            </p>
          )}
          <div className="flex items-baseline justify-between">
            <span className="text-sm text-muted-foreground">
              <T>Cena</T>
            </span>
            <span className="text-2xl font-black" data-box-total>
              {formatCents(total)}
            </span>
          </div>
          <button
            type="button"
            data-box-add
            disabled={problem !== ''}
            onClick={() => {
              onAdd(selection)
              setSelection(EMPTY_BOX)
              onAdded()
            }}
            className="w-full min-h-[52px] rounded-2xl bg-green-600 hover:bg-green-700 text-white font-bold disabled:opacity-50 transition-colors"
          >
            <T>Pridať do košíka</T>
          </button>
        </div>
        </>
        )}
      </div>
    </div>
  )
}
